'use client';

import {
  BarChart,
  CartesianGrid,
  Customized,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { memo, RefObject, useEffect, useMemo, useState } from 'react';

import { getZoneColor } from '@trainme/api/client';

type Props = {
  // time is in seconds (do NOT floor to minutes) to avoid duplicate timestamps
  // which create 1px overlaps/cutoffs after rounding.
  chartData: { time: number; zone: number; key: string }[];
  chartRef: RefObject<HTMLDivElement | null>;
  /** Additional offset applied to the chart's bottom margin (positive pulls axis up). */
  bottomMarginOffset?: number;
};

type PlotBox = { x: number; y: number; width: number; height: number };

const WorkoutRects = memo(function WorkoutRectsComponent({
  plot,
  data,
  maxTime,
  maxZone,
}: {
  plot: PlotBox;
  data: Array<{ time: number; zone: number; key: string }>;
  maxTime: number;
  maxZone: number;
}) {
  if (!data.length || maxTime <= 0 || maxZone <= 0) return null;
  const W = Math.max(1, Math.floor(plot.width));
  const H = Math.max(1, Math.floor(plot.height));

  // Deterministic integer layout from durations (dt). Any leftover pixels are
  // pushed into the LAST zone=0 segment; if none exists, the leftover remains
  // as a right-edge gap (preferred vs distorting visible segments).
  const times = data.map((d) => Number(d.time));
  const zones = data.slice(0, -1).map((d) => Number(d.zone) || 0);
  const dt = times.slice(0, -1).map((t, i) => Math.max(0, times[i + 1] - t));

  const scale = W / maxTime;
  const widths = dt.map((dti) => Math.max(0, Math.round(dti * scale)));

  const sum = () => widths.reduce((a, b) => a + b, 0);
  let total = sum();

  // If too wide, shrink zone=0 segments first (rare).
  if (total > W) {
    let excess = total - W;
    for (let i = 0; i < widths.length && excess > 0; i++) {
      if (zones[i] !== 0) continue;
      const take = Math.min(widths[i], excess);
      widths[i] -= take;
      excess -= take;
    }
    total = sum();
    if (total > W && widths.length > 0) {
      widths[widths.length - 1] = Math.max(
        0,
        widths[widths.length - 1] - (total - W)
      );
    }
  }

  // If short, add only into the LAST zone=0 segment (otherwise keep right gap).
  total = sum();
  if (total < W) {
    const deficit = W - total;
    let lastZero = -1;
    for (let i = zones.length - 1; i >= 0; i--) {
      if (zones[i] === 0) {
        lastZero = i;
        break;
      }
    }
    if (lastZero >= 0) widths[lastZero] += deficit;
  }

  const prefixX: number[] = [0];
  widths.forEach((width) => {
    const last = prefixX[prefixX.length - 1] ?? 0;
    prefixX.push(last + width);
  });
  return (
    <>
      {widths.map((w, i) => {
        const zone = zones[i] ?? 0;
        const x0 = prefixX[i] ?? 0;
        if (w <= 0 || zone <= 0) return null;

        const h = Math.round((zone / (maxZone + 1)) * H);
        if (h <= 0) return null;

        return (
          <rect
            key={`seg-${data[i].key}-${i}-${x0}`}
            x={plot.x + x0}
            y={plot.y + (H - h)}
            width={w}
            height={h}
            fill={getZoneColor(zone)}
            shapeRendering='crispEdges'
          />
        );
      })}
    </>
  );
});
function StepsChartComponent({
  chartData = [],
  chartRef,
  bottomMarginOffset = 0,
}: Props) {
  const [fontSize, setFontSize] = useState(10);
  const [chartHeight, setChartHeight] = useState(100);
  const [chartWidth, setChartWidth] = useState(0);

  useEffect(() => {
    const node = chartRef.current;
    if (!node) return undefined;

    const updateSize = () => {
      const rect = node.getBoundingClientRect();
      const height = rect.height || 10;
      setFontSize(Math.max(8, Math.min(Math.round(height / 5), 11)));
      setChartHeight(height);
      setChartWidth(rect.width);
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, [chartRef]);

  // Calculate evenly spaced ticks for linear labeling
  const timeValues = chartData.map((d) => Number(d.time));
  const minTime = Math.min(...timeValues);
  const maxTime = Math.max(...timeValues);
  const maxZone = Math.max(...chartData.map((d) => Number(d.zone) || 0));

  // Generate evenly spaced ticks at regular intervals (e.g., 10, 20, 30)
  const generateLinearTicks = () => {
    if (minTime === maxTime) return [minTime];
    // Use 10-minute intervals, but in seconds.
    const interval = 10 * 60;
    const roundedMax = Math.ceil(maxTime / interval) * interval;
    const ticks: number[] = [];
    for (let i = interval; i <= roundedMax; i += interval) {
      ticks.push(i);
    }
    return ticks;
  };

  const linearTicks = generateLinearTicks();

  const plotKey = useMemo(
    () => `plot-${chartData[0]?.key ?? 'steps'}`,
    [chartData]
  );

  // fontSize plus a little line-height/clearance, plus a small gap
  // (tickMargin, matched below) between the tick line and the label text —
  // enough for the label to sit close under the axis without overlapping it.
  const tickMargin = 3;
  const axisBottomReserve = Math.max(
    0,
    fontSize + 7 + tickMargin - bottomMarginOffset
  );

  // Single source of truth for the bottom reserve: Recharts' XAxis defaults
  // its own `height` to a fixed 30px, added on top of margin.bottom
  // internally (offset.bottom = margin.bottom + xAxis.height) — regardless
  // of font size. Passing axisBottomReserve as the XAxis's own `height`
  // (instead of trying to cancel that hidden 30px out via margin.bottom, an
  // internal Recharts detail with no reliable public formula) makes it the
  // one true reserved amount, which we also subtract from the plot box
  // handed to the bar layout below — so the bar bottoms and the axis/labels
  // can no longer drift apart across chart sizes. bottomMarginOffset trims a
  // little off that reserve for compact charts that want slightly more room
  // for bars at the cost of tighter labels.
  // Keep margins minimal; hiding the Y axis avoids the left "axis width" offset
  // that can clip the first segment and make it appear narrower.
  // Slight negative right margin helps eliminate the last-pixel gap Recharts can
  // leave at the right edge due to internal rounding.
  const margin = {
    top: 0,
    right: -1,
    bottom: 0,
    left: 0,
  };

  return (
    <div ref={chartRef} className='w-full h-full min-h-20'>
      {chartWidth > 0 && chartHeight > 0 && (
        <ResponsiveContainer
          width='100%'
          height='100%'
          minWidth={1}
          minHeight={1}
        >
          <BarChart
            data={chartData}
            margin={margin}
            barGap={0}
            barCategoryGap={0}
            key={plotKey}
          >
            <CartesianGrid vertical={false} horizontal={false} />
            {/* Draw bars first; axes render after so labels stay visible on top */}
            <Customized
              component={(p: {
                x?: number;
                y?: number;
                width?: number;
                height?: number;
              }) => (
                <WorkoutRects
                  plot={{
                    x: p.x ?? 0,
                    y: p.y ?? 0,
                    width: p.width ?? 0,
                    height: Math.max(0, (p.height ?? 0) - axisBottomReserve),
                  }}
                  data={chartData}
                  maxTime={maxTime}
                  maxZone={maxZone}
                />
              )}
            />
            <XAxis
              type='number'
              dataKey='time'
              domain={[0, maxTime]}
              ticks={linearTicks}
              fontSize={fontSize}
              height={axisBottomReserve}
              axisLine={false}
              tickLine={{ strokeWidth: 1, stroke: '#9ca3af' }}
              tickSize={1}
              tickMargin={tickMargin}
              padding={{ left: 0, right: 0 }}
              tickFormatter={(v) => String(Math.round(Number(v) / 60))}
              key={`x-${chartData[0].key}`}
            />
            <YAxis
              type='number'
              dataKey='zone'
              domain={[0, maxZone + 1]}
              hide
              width={0}
              tick={false}
              axisLine={false}
              label={undefined}
              key={`y-${chartData[0].key}`}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

const StepsChart = memo(StepsChartComponent);
export default StepsChart;
